import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ClipboardList, Eye, X } from "lucide-react";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";
import Pagination from "../../components/shared/Pagination";
import InfinityCaseDetail from "../../components/admin/InfinityCaseDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;
const ALL = "all"; // sentinel for "no filter" (Radix Select disallows empty value)
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// IT order timeline maps the order status to 3 stages.
const STAGES = [
  { key: "placed", label: "Order Placed", match: ["pending", "confirmed"] },
  { key: "submitted", label: "Order Submitted", match: ["processing"] },
  { key: "shipped", label: "Order Shipped", match: ["shipped", "delivered"] },
];
// Deliberately NOT defaulting to stage 0: an unmatched status used to render as
// "Order Placed", so a declined payment looked like a received order — half of TR-25
// item 3. `undefined` means "not on the happy path", and callers show the raw status.
const stageOf = (status) => STAGES.find((s) => s.match.includes(status));
const stageLabel = (status) => stageOf(status)?.label || status || "—";
const stageTone = (status) =>
  stageOf(status) ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-red-200 bg-red-50 text-red-700";

// Payment filter. Unpaid customer orders are hidden by default server-side, because the
// order row is written before the family's card is charged (TR-25 item 3).
const PAYMENT_FILTERS = [
  { key: "paid_only", label: "Hide unpaid", params: {} },
  { key: "include_unpaid", label: "Include unpaid", params: { include_unpaid: "true" } },
  { key: "failed_only", label: "Failed only", params: { include_unpaid: "true", payment_status: "failed" } },
  { key: "pending_only", label: "Awaiting payment", params: { include_unpaid: "true", payment_status: "pending" } },
];

export default function PrintOrders() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Case-centric detail popup (same as admin, read-only): { recordId, focusOrderId }
  const [caseModal, setCaseModal] = useState(null);

  // Filters — same set and semantics as the admin Infinity Touch orders screen.
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState(ALL);   // all | placed | submitted | shipped
  const [payment, setPayment] = useState("paid_only");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchOrders = async (params) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reseller/orders`, {
        params: { channel: "infinity", ...params },
      });
      setOrders(Array.isArray(res.data) ? res.data : res.data?.orders || []);
    } catch (e) {
      toast.error("Failed to load print orders");
    } finally {
      setLoading(false);
    }
  };

  const buildParams = () => {
    const params = {};
    const q = search.trim();
    if (q) params.search = q;
    // The table badges show the 3 stages, so the filter does too — each maps
    // to the raw statuses behind it, which the API takes as a comma list.
    if (stage !== ALL) {
      params.status = (STAGES.find((s) => s.key === stage)?.match || []).join(",");
    }
    Object.assign(params, PAYMENT_FILTERS.find((p) => p.key === payment)?.params || {});
    // Widen each end to the whole day, so "from = to" matches that day.
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      params.startDate = from.toISOString();
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      params.endDate = to.toISOString();
    }
    return params;
  };

  // Re-query whenever any filter changes (search debounced together with the rest).
  // Back to page 1 too, or the client-side slice lands past the end of the new list.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchOrders(buildParams()); }, 350);
    return () => clearTimeout(t);
  }, [search, stage, payment, dateFrom, dateTo]); // eslint-disable-line

  const hasFilters = search || stage !== ALL || payment !== "paid_only" || dateFrom || dateTo;
  const clearFilters = () => { setSearch(""); setStage(ALL); setPayment("paid_only"); setDateFrom(""); setDateTo(""); };

  const viewDetails = (o) =>
    setCaseModal({ recordId: o.memorial_record_id, focusOrderId: o.order_id });

  // Counts follow the same stage mapping as the badge in each row, so the cards
  // and the table can never disagree.
  const countInStage = (key) =>
    orders.filter((o) => stageOf(o.status)?.key === key).length;
  const totalSpend = orders.reduce((sum, o) => sum + Number(o.total || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="print-orders">
      <ModuleReadOnlyNotice module="infinity_touch" />

      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ClipboardList className="w-6 h-6" /> Print Orders
        </h2>
        <p className="text-slate-500">Infinity Touch product orders you placed on behalf of families.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search by order # or family" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={payment} onValueChange={setPayment}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent>
            {PAYMENT_FILTERS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" aria-label="From date" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" aria-label="To date" />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="text-slate-500" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Same four-card summary as the eDisplayroom Orders page. Infinity Touch
          orders are billed to the funeral home at base price with no commission
          (TR-7), so the last card is what was spent, not what was earned. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Total Orders</p>
          <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
        </div>
        <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
          <p className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-1">Order Placed</p>
          <p className="text-2xl font-bold text-yellow-700">{countInStage("placed")}</p>
        </div>
        <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
          <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-1">Order Shipped</p>
          <p className="text-2xl font-bold text-indigo-700">{countInStage("shipped")}</p>
        </div>
        <div className="card-elevated p-4 bg-emerald-50 border border-emerald-100/50">
          <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">Total Spend</p>
          <p className="text-2xl font-bold text-emerald-700">{money(totalSpend)}</p>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Order #</TableHead>
              <TableHead className="font-semibold">Deceased</TableHead>
              <TableHead className="font-semibold">Items</TableHead>
              <TableHead className="font-semibold">Total</TableHead>
              <TableHead className="font-semibold">Placed</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : orders.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">{hasFilters ? "No print orders match these filters." : "No print orders yet."}</TableCell></TableRow>
            ) : orders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((o) => (
              <TableRow key={o.order_id} className="table-row cursor-pointer" onClick={() => viewDetails(o)}>
                <TableCell className="font-medium">{o.order_number}</TableCell>
                <TableCell>{o.deceased_name || o.customer_name || "—"}</TableCell>
                <TableCell>{(o.items || []).length}</TableCell>
                <TableCell>{money(o.total)}</TableCell>
                <TableCell>{fmtDate(o.created_at)}</TableCell>
                <TableCell><Badge variant="outline" className={stageTone(o.status)}>{stageLabel(o.status)}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-600"
                    onClick={(e) => { e.stopPropagation(); viewDetails(o); }}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(orders.length / PAGE_SIZE))}
        onChange={setPage}
        totalItems={orders.length}
        pageSize={PAGE_SIZE}
      />

      {/* Same case-centric popup the admin sees, read-only (no status change). */}
      <InfinityCaseDetail
        open={!!caseModal}
        onClose={() => setCaseModal(null)}
        memorialRecordId={caseModal?.recordId}
        focusOrderId={caseModal?.focusOrderId}
        apiBase="reseller"
        readOnly
      />
    </div>
  );
}
